import type { NativeNotificationPermissionStatus } from '@/lib/native-notification-permission';
import { nativeOsNotificationsEnabled } from '@/lib/native-notification-permission';
import type { NotificationPromptState } from '@/lib/notification-prompt-state';
import {
  getNotificationPromptState,
  hasPendingNotificationPrompt,
} from '@/lib/notification-prompt-state';

function reconcileStoredState(
  storedState: NotificationPromptState | null,
  hasRegisteredDevice: boolean,
  devicesSettled: boolean
): NotificationPromptState | null {
  if (!devicesSettled) return storedState;

  if (hasRegisteredDevice) {
    return 'registered';
  }

  if (storedState === 'registered' || storedState === 'prompted') {
    return 'pending';
  }

  return storedState;
}

function shouldShowForDismissedState(
  storedState: NotificationPromptState | null,
  pendingFromAuth: boolean
): boolean {
  if (storedState === 'dismissed' && !pendingFromAuth) {
    return false;
  }
  return true;
}

export function shouldAutoShowNotificationPrompt(options: {
  userId?: string;
  devicesSettled: boolean;
  hasRegisteredDevice: boolean;
  isNativeApp: boolean;
  nativePermissionStatus: NativeNotificationPermissionStatus;
}): boolean {
  const { userId, devicesSettled, hasRegisteredDevice, isNativeApp, nativePermissionStatus } =
    options;

  if (!userId) return false;

  const pendingFromAuth = hasPendingNotificationPrompt();
  const storedState = reconcileStoredState(
    getNotificationPromptState(userId),
    hasRegisteredDevice,
    devicesSettled
  );

  if (!shouldShowForDismissedState(storedState, pendingFromAuth)) {
    return false;
  }

  // Push notifications are native FCM only — no auto-prompt on web.
  if (!isNativeApp) {
    return false;
  }

  if (nativePermissionStatus === 'unknown') {
    return false;
  }

  if (nativeOsNotificationsEnabled(nativePermissionStatus)) {
    return false;
  }

  return (
    pendingFromAuth ||
    nativePermissionStatus === 'not_determined' ||
    nativePermissionStatus === 'denied' ||
    storedState === null ||
    storedState === 'pending'
  );
}
